/**
 * Page publique « Nos sources » (/sources) : ce que SmartLawyer collecte, par pays,
 * avec le volume de chaque corpus. Données : /api/legalflow/public/sources.
 * Les libellés viennent de l'i18n (sources.items.<collection>), le backend
 * fournit la liste, le pays et les volumes.
 */
import { getDateLocale } from '@/i18n';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';

import { PublicFooter, PublicHeader } from '@/components/jurisprudence';
import { Breadcrumb } from '@/components/jurisprudence/Breadcrumb';

interface Source {
  title: string;
  country: string;
  source: string;
  collections: string[];
  description: string;
  schedule: 'auto' | 'ponctuel';
  documents: number | null;
}

interface Payload {
  countries: Record<string, { label: string; flag: string }>;
  sources: Source[];
  updated_at: string;
}

export default function PublicSources() {
  const { t } = useTranslation();
  const locale = getDateLocale();
  const [data, setData] = useState<Payload | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    fetch('/api/legalflow/public/sources')
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then(setData)
      .catch(() => setFailed(true));
  }, []);

  const nf = new Intl.NumberFormat(locale);
  const byCountry = (data?.sources || []).reduce<Record<string, Source[]>>(
    (acc, s) => {
      (acc[s.country] ||= []).push(s);
      return acc;
    },
    {}
  );
  const total = (data?.sources || []).reduce(
    (n, s) => n + (s.documents || 0),
    0
  );

  return (
    <>
      <Helmet>
        <title>{t('sources.meta_title')}</title>
        <meta name="description" content={t('sources.meta_description')} />
      </Helmet>

      <div className="min-h-screen bg-background flex flex-col">
        <PublicHeader />
        <Breadcrumb
          items={[
            { label: t('menu.home'), to: '/' },
            { label: t('sources.breadcrumb') }
          ]}
        />

        <main className="flex-1">
          <section className="border-b bg-muted/30 py-14 sm:py-16">
            <div className="max-w-4xl mx-auto px-4 sm:px-6">
              <p className="text-xs font-bold uppercase tracking-widest text-primary">
                {t('sources.badge')}
              </p>
              <h1 className="mt-3 text-4xl sm:text-5xl font-bold tracking-tight">
                {t('sources.title')}
              </h1>
              <p className="mt-4 max-w-2xl text-lg text-muted-foreground">
                {t('sources.subtitle')}
              </p>
              {data && total > 0 && (
                <p className="mt-6 text-sm text-muted-foreground">
                  {t('sources.total', {
                    count: total,
                    formatted: nf.format(total)
                  })}
                </p>
              )}
            </div>
          </section>

          <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 space-y-12">
            {failed && (
              <p role="alert" className="text-sm text-muted-foreground">
                {t('sources.error')}
              </p>
            )}
            {!data && !failed && (
              <div className="grid gap-4 sm:grid-cols-2">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div
                    key={i}
                    className="h-40 animate-pulse rounded-2xl bg-muted"
                  />
                ))}
              </div>
            )}

            {data &&
              Object.entries(byCountry).map(([code, sources]) => {
                const c = data.countries[code];
                return (
                  <section key={code} aria-labelledby={`src-${code}`}>
                    <h2
                      id={`src-${code}`}
                      className="text-xl font-semibold flex items-center gap-2"
                    >
                      <span aria-hidden="true">{c?.flag}</span>
                      {t(`sources.countries.${code}`, {
                        defaultValue: c?.label ?? code
                      })}
                    </h2>
                    <div className="mt-4 grid gap-4 sm:grid-cols-2">
                      {sources.map((s) => {
                        const key = s.collections[0];
                        return (
                          <article
                            key={key}
                            className="flex flex-col rounded-2xl border bg-card p-5 shadow-sm"
                          >
                            <h3 className="font-semibold text-foreground">
                              {t(`sources.items.${key}.title`, {
                                defaultValue: s.title
                              })}
                            </h3>
                            <p className="mt-0.5 text-xs text-muted-foreground">
                              {t(`sources.items.${key}.origin`, {
                                defaultValue: s.source
                              })}
                            </p>
                            <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                              {t(`sources.items.${key}.description`, {
                                defaultValue: s.description
                              })}
                            </p>
                            <div className="mt-auto flex items-end justify-between gap-3 pt-4">
                              <div>
                                <p className="text-2xl font-bold tabular-nums text-foreground">
                                  {s.documents != null
                                    ? nf.format(s.documents)
                                    : '—'}
                                </p>
                                <p className="text-xs text-muted-foreground">
                                  {t('sources.documents')}
                                </p>
                              </div>
                              <span className="rounded-full bg-muted px-2.5 py-1 text-xs font-medium">
                                {t(
                                  s.schedule === 'auto'
                                    ? 'sources.schedule_auto'
                                    : 'sources.schedule_manual'
                                )}
                              </span>
                            </div>
                          </article>
                        );
                      })}
                    </div>
                  </section>
                );
              })}

            {data && (
              <p className="text-xs text-muted-foreground">
                {t('sources.updated', {
                  date: new Date(data.updated_at).toLocaleString(locale)
                })}
              </p>
            )}
          </div>
        </main>

        <PublicFooter />
      </div>
    </>
  );
}
